import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface NailTechnicianTemplateProps {
    profile: PublicProfile
}

export function NailTechnicianTemplate({ profile }: NailTechnicianTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b" style={{ borderColor: `${colors.colors.primary}33` }}>
                <div className="max-w-6xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            {profile.profile_image && (
                                <div className="w-12 h-12 rounded-full p-0.5" style={{ background: `linear-gradient(to bottom right, ${colors.colors.primary}, ${colors.colors.secondary})` }}>
                                    <img
                                        src={profile.profile_image}
                                        alt={profile.business_name}
                                        className="w-full h-full rounded-full object-cover"
                                    />
                                </div>
                            )}
                            <div>
                                <h1 className="text-xl font-bold">{profile.business_name}</h1>
                                {profile.tagline && (
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                        {profile.tagline}
                                    </p>
                                )}
                            </div>
                        </div>
                        <nav className="hidden md:flex items-center space-x-6">
                            <a href="#about" className="hover:opacity-80 transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.beauty.about')}
                            </a>
                            <a href="#services" className="hover:opacity-80 transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.beauty.services')}
                            </a>
                            <a href="#gallery" className="hover:opacity-80 transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.beauty.gallery')}
                            </a>
                            <a href="#contact" className="hover:opacity-80 transition-colors cursor-pointer" style={{ color: colors.colors.textMuted }}>
                                {t('template.beauty.contact')}
                            </a>
                        </nav>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="py-16 px-4">
                <div className="max-w-6xl mx-auto">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div className="order-2 md:order-1">
                            <p className="font-semibold mb-2" style={{ color: colors.colors.primary }}>
                                Welcome to My Studio
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6 leading-tight"
                                style={{ fontFamily: typography.heading }}
                            >
                                Creating Beautiful Nails for Confident You
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                {profile.bio || 'With over 8 years of experience in nail art and care, I specialize in creating stunning, long-lasting nail designs that express your unique style.'}
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <button
                                    onClick={() => setIsBookingOpen(true)}
                                    className="inline-flex items-center px-6 py-3 rounded-full font-semibold shadow-lg hover:opacity-90 transition-all cursor-pointer"
                                    style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                                >
                                    {t('template.beauty.bookNow')}
                                    <span className="ml-2">→</span>
                                </button>
                                <a
                                    href="#gallery"
                                    className="inline-flex items-center px-6 py-3 border-2 rounded-full font-semibold hover:opacity-90 transition-all cursor-pointer"
                                    style={{ borderColor: colors.colors.primary, color: colors.colors.primary }}
                                >
                                    {t('template.beauty.viewGallery')}
                                </a>
                            </div>
                        </div>
                        <div className="order-1 md:order-2">
                            <div className="relative">
                                <div
                                    className="absolute -top-4 -left-4 w-72 h-72 rounded-full blur-3xl"
                                    style={{ backgroundColor: `${colors.colors.secondary}50` }}
                                />
                                <div
                                    className="absolute -bottom-4 -right-4 w-72 h-72 rounded-full blur-3xl"
                                    style={{ backgroundColor: `${colors.colors.primary}50` }}
                                />
                                {profile.cover_image ? (
                                    <img
                                        src={profile.cover_image}
                                        alt="Nail Art Studio"
                                        className="relative rounded-3xl shadow-2xl w-full object-cover"
                                    />
                                ) : profile.profile_image ? (
                                    <img
                                        src={profile.profile_image}
                                        alt={profile.business_name}
                                        className="relative rounded-3xl shadow-2xl w-full object-cover"
                                    />
                                ) : (
                                    <div
                                        className="relative rounded-3xl shadow-2xl w-full h-96 flex items-center justify-center"
                                        style={{ backgroundColor: colors.colors.secondary }}
                                    >
                                        <span className="text-6xl">💅</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            {profile.bio && (
                <section id="about" className="py-16 px-4 bg-white">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p className="font-semibold mb-2" style={{ color: colors.colors.primary }}>
                                {t('template.beauty.aboutMe')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl font-bold"
                                style={{ fontFamily: typography.heading }}
                            >
                                Meet Your Nail Artist
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 gap-12 items-center">
                            <div className="flex justify-center">
                                <div className="relative">
                                    {profile.profile_image ? (
                                        <img
                                            src={profile.profile_image}
                                            alt={profile.business_name}
                                            className="w-64 h-64 md:w-80 md:h-80 rounded-3xl shadow-xl object-cover"
                                        />
                                    ) : (
                                        <div
                                            className="w-64 h-64 md:w-80 md:h-80 rounded-3xl shadow-xl flex items-center justify-center"
                                            style={{ backgroundColor: colors.colors.secondary }}
                                        >
                                            <span className="text-6xl">💅</span>
                                        </div>
                                    )}
                                    <div
                                        className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-lg p-4"
                                        style={{ backgroundColor: colors.colors.background }}
                                    >
                                        <div className="flex items-center space-x-2">
                                            <span className="text-2xl" style={{ color: colors.colors.primary }}>🏆</span>
                                            <div>
                                                <p className="text-sm font-bold">8+ Years</p>
                                                <p className="text-xs" style={{ color: colors.colors.textMuted }}>
                                                    Experience
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold mb-4">Hi, I'm {profile.business_name}!</h3>
                                <p className="mb-4 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    {profile.bio}
                                </p>
                                {profile.location && (
                                    <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                        📍 {profile.location}
                                    </p>
                                )}
                                <div className="grid grid-cols-2 gap-4 mt-6">
                                    <div
                                        className="rounded-xl p-4"
                                        style={{ backgroundColor: `${colors.colors.primary}11` }}
                                    >
                                        <span className="text-xl mb-2 block" style={{ color: colors.colors.primary }}>📜</span>
                                        <p className="font-semibold">Licensed Technician</p>
                                        <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                            State Certified
                                        </p>
                                    </div>
                                    <div
                                        className="rounded-xl p-4"
                                        style={{ backgroundColor: `${colors.colors.primary}11` }}
                                    >
                                        <span className="text-xl mb-2 block" style={{ color: colors.colors.primary }}>💖</span>
                                        <p className="font-semibold">500+ Happy Clients</p>
                                        <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                            5-Star Reviews
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Services Section */}
            {profile.services && profile.services.length > 0 && (
                <section
                    id="services"
                    className="py-16 px-4"
                    style={{ backgroundColor: colors.colors.background }}
                >
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p className="font-semibold mb-2" style={{ color: colors.colors.primary }}>
                                {t('template.beauty.whatIOffer')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl font-bold"
                                style={{ fontFamily: typography.heading }}
                            >
                                {t('template.beauty.servicesAndPricing')}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {profile.services.map((service) => (
                                <div
                                    key={service.id}
                                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border"
                                    style={{ borderColor: `${colors.colors.primary}22` }}
                                >
                                    <div className="flex items-start justify-between mb-4">
                                        <div
                                            className="w-14 h-14 rounded-xl flex items-center justify-center"
                                            style={{ backgroundColor: `${colors.colors.primary}11` }}
                                        >
                                            <span style={{ color: colors.colors.primary }}>✨</span>
                                        </div>
                                        {service.price_display !== 'hidden' && service.price && (
                                            <span className="font-bold text-xl" style={{ color: colors.colors.primary }}>
                                                ${service.price.toFixed(0)}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="text-xl font-bold mb-2">{service.name}</h3>
                                    {service.description && (
                                        <p className="mb-4 text-sm" style={{ color: colors.colors.textMuted }}>
                                            {service.description}
                                        </p>
                                    )}
                                    {service.duration_minutes && (
                                        <div className="flex items-center text-sm" style={{ color: colors.colors.textMuted }}>
                                            <span className="mr-2">⏱</span>
                                            <span>{service.duration_minutes} {t('template.common.min')}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Gallery Section */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section id="gallery" className="py-16 px-4 bg-white">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-12">
                            <p className="font-semibold mb-2" style={{ color: colors.colors.primary }}>
                                {t('template.beauty.myWork')}
                            </p>
                            <h2
                                className="text-3xl md:text-4xl font-bold"
                                style={{ fontFamily: typography.heading }}
                            >
                                {t('template.beauty.portfolioGallery')}
                            </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={8}
                            enableLightbox={true}
                            showCaptions={true}
                            aspectRatio="square"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* Work Hours Section */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-16 px-4" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-4xl mx-auto">
                        <div
                            className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border"
                            style={{ borderColor: `${colors.colors.primary}22` }}
                        >
                            <div className="text-center mb-8">
                                <span className="text-4xl mb-4 block" style={{ color: colors.colors.primary }}>
                                    🕐
                                </span>
                                <h2
                                    className="text-2xl md:text-3xl font-bold"
                                    style={{ fontFamily: typography.heading }}
                                >
                                    {t('template.beauty.studioHours')}
                                </h2>
                                {profile.location && (
                                    <p className="mt-2" style={{ color: colors.colors.textMuted }}>
                                        {t('template.beauty.visitMyStudio')} {profile.location}
                                    </p>
                                )}
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    {profile.availability.slice(0, 4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: `${colors.colors.primary}22` }}
                                        >
                                            <span className="font-semibold">{day.day_name}</span>
                                            <span style={{ color: colors.colors.textMuted }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-4">
                                    {profile.availability.slice(4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: `${colors.colors.primary}22` }}
                                        >
                                            <span className="font-semibold">{day.day_name}</span>
                                            <span
                                                style={{
                                                    color: day.is_available ? colors.colors.textMuted : colors.colors.primary,
                                                    fontWeight: day.is_available ? 'normal' : 'semibold',
                                                }}
                                            >
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.closed')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-8 text-center">
                                <p style={{ color: colors.colors.textMuted }}>
                                    <span className="mr-2">ℹ️</span>
                                    {t('template.beauty.appointmentsPreferred')}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact Section */}
            <section
                id="contact"
                className="py-16 px-4"
                style={{ backgroundColor: colors.colors.background }}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="font-semibold mb-2" style={{ color: colors.colors.primary }}>
                            {t('template.beauty.getInTouch')}
                        </p>
                        <h2
                            className="text-3xl md:text-4xl font-bold"
                            style={{ fontFamily: typography.heading }}
                            >
                                {t('template.beauty.bookYourAppointment')}
                            </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-12">
                        <div>
                            <h3 className="text-2xl font-bold mb-6">{t('template.beauty.letsConnect')}</h3>
                            <div className="space-y-6">
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.primary}11` }}
                                        >
                                            <span style={{ color: colors.colors.primary }}>📍</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">{t('template.beauty.location')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.primary}11` }}
                                        >
                                            <span style={{ color: colors.colors.primary }}>📞</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">{t('template.beauty.phone')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.primary}11` }}
                                        >
                                            <span style={{ color: colors.colors.primary }}>✉️</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold">{t('template.beauty.email')}</h4>
                                            <p style={{ color: colors.colors.textMuted }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Social Links */}
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="mt-8">
                                    <h4 className="font-semibold mb-4">{t('template.beauty.followMe')}</h4>
                                    <div className="flex space-x-4">
                                        {profile.social_links.slice(0, 4).map((link) => (
                                            <a
                                                key={link.platform}
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                aria-label={`Follow on ${link.platform}`}
                                                className="w-12 h-12 rounded-xl flex items-center justify-center transition-all hover:opacity-80"
                                                style={{
                                                    backgroundColor: `${colors.colors.primary}11`,
                                                    color: colors.colors.primary,
                                                }}
                                            >
                                                {link.platform === 'instagram' && '📷'}
                                                {link.platform === 'facebook' && '📘'}
                                                {link.platform === 'tiktok' && '🎵'}
                                                {link.platform === 'pinterest' && '📌'}
                                                {!['instagram', 'facebook', 'tiktok', 'pinterest'].includes(link.platform) && '🔗'}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Contact CTA */}
                        <div className="flex flex-col justify-center">
                            <div
                                className="bg-white rounded-2xl p-8 shadow-lg"
                                style={{ backgroundColor: colors.colors.background }}
                            >
                                <h3 className="text-xl font-bold mb-6">{t('template.beauty.readyToBook')}</h3>
                                <div className="flex flex-col gap-3">
                                    {profile.phone && (
                                        <a
                                            href={`tel:${profile.phone}`}
                                            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-lg transition-opacity hover:opacity-90"
                                            style={{
                                                backgroundColor: colors.colors.primary,
                                                color: colors.colors.secondary,
                                            }}
                                        >
                                            📞 {t('template.beauty.callNow')}
                                        </a>
                                    )}
                                    {profile.email && (
                                        <a
                                            href={`mailto:${profile.email}`}
                                            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold text-lg transition-opacity hover:opacity-90"
                                            style={{
                                                backgroundColor: colors.colors.secondary,
                                                color: colors.colors.primary,
                                            }}
                                        >
                                            ✉️ {t('template.beauty.emailMe')}
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-8 px-4" style={{ backgroundColor: colors.colors.text, color: colors.colors.background }}>
                <div className="max-w-6xl mx-auto text-center">
                    <div className="flex items-center justify-center space-x-3 mb-4">
                        {profile.profile_image && (
                            <div
                                className="w-10 h-10 rounded-full p-0.5"
                                style={{ background: `linear-gradient(to bottom right, ${colors.colors.primary}, ${colors.colors.secondary})` }}
                            >
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="w-full h-full rounded-full object-cover"
                                />
                            </div>
                        )}
                        <span className="font-bold">{profile.business_name}</span>
                    </div>
                    <p className="mb-4" style={{ color: `${colors.colors.background}99` }}>
                        © {new Date().getFullYear()} {profile.business_name}. {t('template.beauty.poweredBy')}
                    </p>
                    {profile.social_links && profile.social_links.length > 0 && (
                        <div className="flex justify-center space-x-4">
                            {profile.social_links.slice(0, 3).map((link) => (
                                <a
                                    key={link.platform}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`Follow on ${link.platform}`}
                                    className="hover:opacity-80 transition-colors"
                                    style={{ color: `${colors.colors.background}99` }}
                                >
                                    {link.platform === 'instagram' && '📷'}
                                    {link.platform === 'facebook' && '📘'}
                                    {link.platform === 'tiktok' && '🎵'}
                                    {!['instagram', 'facebook', 'tiktok'].includes(link.platform) && '🔗'}
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default NailTechnicianTemplate;
